import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Divider,
  Box,
  Grow,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import BetaDisclaimer from './BetaDisclaimer';
import ElectricBorder from '../reactBits/ElectricBorder';

declare const __APP_VERSION__: string;

const STORAGE_KEY = 'betaAcceptedVersion';
const COUNTDOWN_SECONDS = 10;

export const BetaAcceptanceDialog: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [countdown, setCountdown] = useState(COUNTDOWN_SECONDS);
  const [showButton, setShowButton] = useState(false);

  useEffect(() => {
    // Skip beta dialog during Cypress tests to prevent modal overlay issues
    if ((window as any).Cypress) {
      return;
    }

    const accepted = localStorage.getItem(STORAGE_KEY);
    if (accepted !== __APP_VERSION__) {
      setOpen(true);
      setCountdown(COUNTDOWN_SECONDS);
      setShowButton(false);
      const t = setTimeout(() => setShowButton(true), 2800);
      return () => clearTimeout(t);
    }
  }, []);

  useEffect(() => {
    if (!open || countdown <= 0) return;
    const timer = setTimeout(() => setCountdown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [open, countdown]);

  const handleAccept = () => {
    localStorage.setItem(STORAGE_KEY, __APP_VERSION__);
    setOpen(false);
  };

  return (
    <Dialog
      open={open}
      disableEscapeKeyDown
      slotProps={{
        backdrop: {
          sx: {
            backdropFilter: 'blur(6px)',
          },
        },
      }}
      PaperProps={{
        sx: {
          maxWidth: 520,
          overflow: 'visible',
          border: 0,
          borderRadius: 3,
          bgcolor: 'transparent',
          boxShadow: 'none',
        },
      }}
    >
      <ElectricBorder
        speed={0.62}
        chaos={0.075}
        borderRadius={24}
        style={{ borderRadius: 24 }}
      >
        <Box
          sx={{
            position: 'relative',
            borderRadius: 3,
            bgcolor: 'background.paper',
            backgroundImage: (theme) =>
              `linear-gradient(145deg, ${alpha(theme.palette.warning.main, 0.055)}, transparent 32%)`,
            overflow: 'hidden',
            border: (theme) =>
              `1px solid ${alpha(theme.palette.warning.light, 0.7)}`,
            boxShadow: (theme) =>
              `0 28px 80px ${alpha(theme.palette.common.black, 0.58)}, inset 0 1px 0 ${alpha(theme.palette.common.white, 0.06)}`,
          }}
        >
          <DialogContent sx={{ pt: 3, pb: 2 }}>
            <Typography
              variant="h6"
              gutterBottom
              sx={{
                fontWeight: 750,
                mb: 2,
                textAlign: 'center',
                color: 'text.primary',
                letterSpacing: '0.01em',
                textShadow: (theme) =>
                  `0 1px 16px ${alpha(theme.palette.common.black, 0.35)}`,
              }}
            >
              Before you continue
            </Typography>
            <Divider
              sx={{
                mb: 3,
                borderColor: (theme) => alpha(theme.palette.warning.main, 0.22),
              }}
            />
            <BetaDisclaimer animate />
          </DialogContent>
          <DialogActions
            sx={{
              px: 3,
              pb: 3,
              pt: 2,
              justifyContent: 'center',
              minHeight: 72,
              borderTop: (theme) =>
                `1px solid ${alpha(theme.palette.warning.main, 0.14)}`,
              bgcolor: (theme) => alpha(theme.palette.common.black, 0.08),
            }}
          >
            <Grow
              in={showButton}
              timeout={600}
              style={{ transformOrigin: '50% 0 0' }}
            >
              <Button
                variant="contained"
                disabled={countdown > 0}
                onClick={handleAccept}
                sx={(theme) => ({
                  minWidth: 160,
                  minHeight: 42,
                  fontWeight: 800,
                  color: theme.palette.getContrastText(
                    theme.palette.warning.main
                  ),
                  bgcolor: theme.palette.warning.main,
                  border: `1px solid ${theme.palette.warning.light}`,
                  boxShadow: `0 7px 22px ${alpha(theme.palette.warning.main, 0.22)}`,
                  '&:hover': {
                    bgcolor: theme.palette.warning.light,
                    boxShadow: `0 9px 28px ${alpha(theme.palette.warning.main, 0.32)}`,
                  },
                  '&.Mui-disabled': {
                    color: theme.palette.text.disabled,
                    bgcolor: alpha(theme.palette.warning.main, 0.12),
                    borderColor: alpha(theme.palette.warning.main, 0.24),
                  },
                })}
              >
                {countdown > 0 ? `Accept (${countdown})` : 'Accept'}
              </Button>
            </Grow>
          </DialogActions>
        </Box>
      </ElectricBorder>
    </Dialog>
  );
};
